import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import shipmentClientService from '../../services/ShipmentClientService.js';
import { Users, Pencil, ArrowLeft, DollarSign, FileText, Send } from 'lucide-react';
import { PageLayout, Button, StatusBadge, DetailCard, DetailRow } from '../common/index.js';

const ViewShipmentClient = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClient();
  }, [clientId]);

  const fetchClient = async () => {
    try {
      const response = await shipmentClientService.getById(clientId);
      setClient(response.data);
    } catch (err) {
      Alert.error('Failed to load client details');
      console.error(err);
      navigate('/shipment-clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      setDownloading(true);
      await shipmentClientService.downloadClientInvoice(clientId);
      Alert.success('Invoice downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download invoice');
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const handleSendInvoice = async () => {
    const confirmed = await Alert.confirm(
      `Send invoice to ${client.email}?`,
      'Send Invoice'
    );
    
    if (confirmed) {
      try {
        setSending(true);
        await shipmentClientService.sendClientInvoice(clientId);
        Alert.success('Invoice sent successfully!');
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to send invoice');
      } finally {
        setSending(false);
      }
    }
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <PageLayout title="Shipment Client Details" icon={Users}>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-sm text-gray-600">Loading client details...</p>
        </div>
      </PageLayout>
    );
  }

  if (!client) {
    return null;
  }

  return (
    <PageLayout
      title="Shipment Client Details"
      icon={Users}
      actions={
        <>
          <Button icon={DollarSign} onClick={() => navigate(`/shipment-clients/batch-bill/${clientId}`)}>
            Batch Payment
          </Button>
          <Button variant="outline" icon={FileText} onClick={handleDownloadInvoice} loading={downloading}>
            Download Invoice
          </Button>
          <Button variant="outline" icon={Send} onClick={handleSendInvoice} loading={sending}>
            Email Invoice
          </Button>
          <Button variant="outline" icon={Pencil} onClick={() => navigate(`/shipment-clients/edit/${clientId}`)}>
            Edit
          </Button>
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/shipment-clients')}>
            Back
          </Button>
        </>
      }
    >
                    {/* Basic Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Full Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{client.full_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Company Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{client.company_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">GST Number</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{client.gst_number || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Contact Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Email</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{client.email}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Contact Number</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{client.contact}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Address</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{client.address}</p>
                        </div>
                      </div>
                    </div>

                    {/* Account Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Account Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        <div className="bg-brand-light p-4 rounded-sm border border-blue-200">
                          <label className="block text-xs sm:text-sm font-medium text-brand mb-2">Total Amount</label>
                          <p className="text-xl sm:text-2xl font-bold text-brand">
                            ₹{client.total_amount?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-sm border border-green-200">
                          <label className="block text-xs sm:text-sm font-medium text-green-700 mb-2">Paid Amount</label>
                          <p className="text-xl sm:text-2xl font-bold text-green-600">
                            ₹{client.paid_amount?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                        <div className="bg-red-50 p-4 rounded-sm border border-red-200">
                          <label className="block text-xs sm:text-sm font-medium text-red-700 mb-2">Due Amount</label>
                          <p className="text-xl sm:text-2xl font-bold text-red-600">
                            ₹{client.due_amount?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Payment Action Button */}
                      {client.due_amount > 0 && (
                        <div className="mt-4">
                          <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-yellow-800">
                                Outstanding Balance: ₹{client.due_amount?.toFixed(2)}
                              </p>
                              <p className="text-xs text-yellow-600 mt-1">
                                Click "Batch Payment" to process payments for unpaid shipments
                              </p>
                            </div>
                            <button
                              onClick={() => navigate(`/shipment-clients/batch-bill/${clientId}`)}
                              className="ml-4 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 transition-all font-medium text-sm whitespace-nowrap"
                            >
                              <DollarSign className="w-4 h-4" />
                              Process Payment
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Invoice Actions */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Invoice & Statements
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-sm border border-gray-200">
                        <p className="text-sm text-gray-600 mb-4">
                          Download or email a complete statement of account for this client including all shipments and payment details.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <button
                            onClick={handleDownloadInvoice}
                            disabled={downloading}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-brand text-white rounded-sm hover:bg-brand-hover transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FileText className="w-4 h-4" />
                            {downloading ? 'Downloading...' : 'Download Statement'}
                          </button>
                          <button
                            onClick={handleSendInvoice}
                            disabled={sending}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-sm hover:bg-purple-700 transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Send className="w-4 h-4" />
                            {sending ? 'Sending...' : 'Email Statement'}
                          </button>
                        </div>
                        {client.email && (
                          <p className="text-xs text-gray-500 mt-3">
                            Statement will be sent to: <span className="font-medium text-gray-700">{client.email}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Status
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Current Status</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              client.status === 'active' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {capitalizeStatus(client.status)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Client ID</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{client.id}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{client.created_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(client.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Updated</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(client.updated_at)}</p>
                        </div>
                      </div>
                    </div>
    </PageLayout>
  );
};

export default ViewShipmentClient;